import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { FAQ_CATEGORIES, FAQ_FLAT } from "@/data/faq";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Faq = () => {
  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_FLAT.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a.join("\n\n"),
        },
      })),
    }),
    []
  );

  return (
    <>
      <Seo
        title="FAQ fibre optique : raccordement, inspection et contrôle qualité | ATLAS THD"
        description="Découvrez les réponses d'ATLAS THD sur les raccordements FTTO et FTTH, les audits qualité, les contrôles de conformité, les méthodes d'inspection et les remontées VQSE."
        jsonLd={faqJsonLd}
        breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "FAQ" }]}
      />

      {/* HERO */}
      <section className="border-b border-line bg-cloud" data-testid="faq-hero">
        <div className="bg-blueprint">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
            <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "FAQ" }]} />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-steel"
            >
              Questions fréquentes
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl"
            >
              Questions fréquentes sur la fibre optique et le contrôle qualité
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 max-w-3xl text-lg leading-relaxed text-steel"
              data-testid="faq-intro"
            >
              Vous souhaitez faire réaliser un raccordement fibre optique,
              organiser une inspection technique ou confier une mission de
              contrôle qualité ? ATLAS THD répond à vos questions sur les
              raccordements FTTO et FTTH, les audits de conformité, les
              méthodes de contrôle et les interventions terrain.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium"
            >
              <Link to="/terrain-fibre" data-testid="faq-link-terrain" className="text-terrain underline decoration-terrain/40 underline-offset-4 transition-colors hover:text-midnight">
                Raccordement FTTO / FTTH
              </Link>
              <Link to="/qualite-controle" data-testid="faq-link-qualite" className="text-terrain underline decoration-terrain/40 underline-offset-4 transition-colors hover:text-midnight">
                Inspection &amp; contrôle qualité
              </Link>
              <Link to="/realisations" data-testid="faq-link-realisations" className="text-terrain underline decoration-terrain/40 underline-offset-4 transition-colors hover:text-midnight">
                Réalisations
              </Link>
              <Link to="/contact" data-testid="faq-link-contact" className="text-terrain underline decoration-terrain/40 underline-offset-4 transition-colors hover:text-midnight">
                Contact
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ANCRES CATÉGORIES */}
      <div className="sticky top-16 z-40 border-b border-line bg-white/95 backdrop-blur-sm" data-testid="faq-anchor-nav">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
          {FAQ_CATEGORIES.map((cat) => (
            <a
              key={cat.id}
              href={`#${cat.anchor}`}
              data-testid={`faq-anchor-${cat.id}`}
              className="whitespace-nowrap border border-line px-4 py-2 text-xs font-semibold text-midnight transition-colors duration-200 hover:border-midnight hover:bg-midnight hover:text-white"
            >
              {cat.title}
            </a>
          ))}
        </div>
      </div>

      {/* CATÉGORIES */}
      <section className="bg-white py-16 lg:py-20" data-testid="faq-content">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {FAQ_CATEGORIES.map((cat, catIndex) => (
            <motion.div
              key={cat.id}
              {...fadeUp}
              transition={{ duration: 0.5 }}
              id={cat.anchor}
              className={`scroll-mt-32 ${catIndex > 0 ? "mt-16" : ""}`}
              data-testid={`faq-category-${cat.id}`}
            >
              <div className="flex items-center gap-4">
                <span className="h-1.5 w-10 bg-terrain" />
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-midnight sm:text-3xl">
                  {cat.title}
                </h2>
              </div>
              <Accordion type="single" collapsible className="mt-8 border border-line">
                {cat.questions.map((item, i) => (
                  <AccordionItem
                    key={item.q}
                    value={`${cat.id}-${i}`}
                    className="border-line px-5 last:border-b-0 sm:px-7"
                    data-testid={`faq-item-${cat.id}-${i}`}
                  >
                    <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-midnight hover:no-underline sm:text-lg">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pb-6 text-sm leading-relaxed text-steel sm:text-base">
                      {item.a.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      <p>
                        <Link
                          to={cat.link.to}
                          data-testid={`faq-inline-link-${cat.id}-${i}`}
                          className="inline-flex items-center gap-1.5 font-display text-sm font-bold text-midnight underline decoration-terrain decoration-2 underline-offset-4 transition-colors hover:text-terrain"
                        >
                          {cat.link.label}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="mt-6 flex flex-col items-start justify-between gap-4 border border-line bg-cloud p-6 sm:flex-row sm:items-center">
                <p className="text-sm font-medium text-midnight">
                  Une question sur cette thématique ?
                </p>
                <Link
                  to={cat.cta.to}
                  data-testid={`faq-cta-${cat.id}`}
                  className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors hover:text-terrain"
                >
                  {cat.cta.label}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ))}

          {/* CTA FINAL */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-20 bg-midnight p-8 text-white sm:p-12"
            data-testid="faq-final-cta"
          >
            <div className="bg-blueprint-dark -m-8 p-8 sm:-m-12 sm:p-12">
              <h2 className="max-w-2xl font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                Vous avez un projet de raccordement, d'inspection ou de contrôle
                qualité ? Échangeons sur vos besoins.
              </h2>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  data-testid="faq-final-devis-button"
                  className="group flex items-center justify-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-white"
                >
                  Parlez-nous de votre besoin
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <a
                  href="mailto:Contact.pro@atlasthd.com"
                  data-testid="faq-final-contact-button"
                  className="flex items-center justify-center gap-2 border border-white/30 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-white hover:bg-white hover:text-midnight"
                >
                  <Mail className="h-4 w-4" />
                  Contacter ATLAS THD
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Faq;
